import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { onPaywallHit } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * Persistent, dismiss-nothing banner shown across every authenticated page
 * once the 30-day trial has expired. Reads still work; writes return 402.
 * When a 402 comes back from any mutation we also nudge with a toast and
 * — if the user isn't already on /upgrade — soft-redirect them there.
 */
export default function PaywallBanner() {
  const { billing, refreshBilling } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const off = onPaywallHit(() => {
      refreshBilling();
      toast.error("Your free trial has ended. Please choose a plan to continue.");
      if (!loc.pathname.startsWith("/upgrade") && !loc.pathname.startsWith("/billing")) {
        nav("/upgrade");
      }
    });
    return off;
  }, [nav, loc.pathname, refreshBilling]);

  if (billing?.status !== "trial_expired") return null;

  return (
    <div
      data-testid="paywall-banner"
      className="w-full bg-gradient-to-r from-hearth-clay/15 via-hearth-cream to-hearth-amber/15 border-b border-hearth-clay/30 px-4 py-2 text-sm text-hearth-ink flex items-center justify-center gap-3"
    >
      <Sparkles size={14} className="text-hearth-clayDark shrink-0" />
      <span>
        Your 30-day trial has ended — <span className="font-medium">read-only mode</span>. Pick a plan to keep adding.
      </span>
      <Link
        to="/upgrade"
        data-testid="paywall-banner-upgrade"
        className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-4 py-1 text-xs font-medium whitespace-nowrap"
      >
        Upgrade
      </Link>
    </div>
  );
}
